import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { formatDistance } from "date-fns";
import { DataTable } from "@common/components/Table/DataTable";
import { stipendsEmployeeColumns } from "./columns";
import { BudgetProgressBar } from "./BudgetProgressBar";
import { isStipendExpired, isStipendUpcoming } from "@/utils/date-time";
import { StipendWalletResponse } from "@common/utils/graphql";
import { Plus } from "@phosphor-icons/react";

interface StipendsCardProps {
  stipend: StipendWalletResponse;
  onClaim: (stipendWallet: StipendWalletResponse) => void;
  onViewDetails: () => void;
}

const COLORS = ["#24BA2A", "#466FFF", "#B6AB4A"];

// Function to get hex color based on ID
const getHexFromId = (id: number) => {
  const idx = id % COLORS.length;
  return COLORS[idx];
};

export const StipendsCard = ({
  stipend,
  onClaim,
  onViewDetails,
}: StipendsCardProps) => {
  const isExpired = isStipendExpired(stipend.expiresAt);
  const isDisabled = !stipend.stipendDetail.isEnabled;
  const isUpcoming = isStipendUpcoming(stipend.startsAt);
  const isInactive = isExpired || isDisabled;
  const isActive = !isInactive && !isUpcoming;

  const header = (
    <div className="flex flex-row justify-between">
      <CardHeader className="flex items-start justify-start">
        <CardTitle>{stipend?.stipendDetail?.name}</CardTitle>
        <CardDescription>
          <p className="line-clamp-2">
            {stipend?.stipendDetail.longDetails || "No details"}
          </p>
          <div>
            {stipend?.stipendDetail?.cycleEndDate
              ? "Renews " +
                formatDistance(
                  stipend?.stipendDetail?.cycleEndDate,
                  new Date(),
                  { addSuffix: true },
                )
              : "No renewal"}
          </div>
        </CardDescription>
      </CardHeader>
      <div className="flex items-center justify-center gap-5 pr-5">
        {isActive && (
          <Button variant="secondary" onClick={() => onClaim(stipend)}>
            <Plus className="mr-2 size-4" />
            Submit Claim
          </Button>
        )}
        {!isUpcoming && (
          <Button variant="secondary" onClick={() => onViewDetails()}>
            See All Claims
          </Button>
        )}
        {stipend.expenses.length > 0 && (
          <AccordionTrigger className="flex items-center justify-center" />
        )}
      </div>
    </div>
  );

  const body = (
    <CardContent className="mx-6 justify-between border-t bg-transparent p-0 py-4">
      <DataTable
        columns={stipendsEmployeeColumns as any}
        data={stipend?.expenses}
      />
    </CardContent>
  );

  const footer = (
    <CardFooter className="mx-6 flex justify-between border-t bg-transparent p-0 pb-6 pt-4">
      <BudgetProgressBar
        stipend={stipend}
        isExpired={isExpired}
        isDisabled={isDisabled}
      />
    </CardFooter>
  );

  return (
    <div className="relative z-10 mt-5 overflow-hidden rounded-lg bg-border p-px">
      <div
        className="absolute -left-24 -top-40 -z-20 h-80 w-80 rounded-full blur-3xl"
        style={{
          backgroundColor: isInactive ? "#292929cc" : getHexFromId(stipend.id),
        }}
      />
      <Card className="rounded-[7px] border-none">
        <Accordion type="single" collapsible>
          <AccordionItem value="item-1" className="border-0">
            {header}
            <AccordionContent>{body}</AccordionContent>
          </AccordionItem>
        </Accordion>
        {footer}
      </Card>
    </div>
  );
};
